import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { billingApi } from '@/api';
import type { SalesReturnRequest } from '@/types/billing';
import { PageHeader } from '@/components/layout/PageHeader';
import { PosApprovalModal } from '@/components/pos/billing/PosApprovalModal';
import { getApiErrorMessage } from '@/utils/apiError';
import { parseApprovalId } from '@/utils/posApproval';
import { formatCurrency } from '@/utils/format';
import { useAppSelector } from '@/hooks/useAppSelector';
import { notify } from '@/core/toast/notify';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const card = 'rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900';
const lbl = 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500';
const btn = (v: 'primary' | 'ghost' | 'danger') =>
  `inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all disabled:opacity-50 focus:outline-none focus:ring-2 ${
    v === 'primary' ? 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500/40'
    : v === 'danger' ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500/40'
    : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 focus:ring-slate-400/30'
  }`;

/* ─── Types ──────────────────────────────────────────────────────────────── */
interface InvoiceItem {
  id: number;
  productId: number;
  productName: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

interface InvoiceDetail {
  id: number;
  invoiceNumber: string;
  totalAmount: number;
  storeId: number;
  items: InvoiceItem[];
  currencyCode?: string;
}

type ReturnType = 'RETURN' | 'EXCHANGE';
type RefundMethod = 'CASH' | 'UPI' | 'CARD' | 'WALLET';

interface ReturnLine {
  itemId: number;
  productId: number;
  productName: string;
  maxQty: number;
  returnQty: number;
  unitPrice: number;
  selected: boolean;
}

/* ─── Invoice Lookup ─────────────────────────────────────────────────────── */
function InvoiceLookup({
  storeId,
  onFound,
}: {
  storeId: number;
  onFound: (inv: InvoiceDetail) => void;
}) {
  const [invoiceRef, setInvoiceRef] = useState('');
  const [loading, setLoading] = useState(false);

  const lookup = async () => {
    if (!invoiceRef.trim()) return;
    setLoading(true);
    try {
      const inv = await billingApi.lookupInvoiceForReturn(storeId, invoiceRef.trim());
      onFound(inv as unknown as InvoiceDetail);
    } catch {
      notify.error('Invoice not found for this store');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={card}>
      <h2 className="mb-4 text-base font-semibold text-slate-900 dark:text-white">Find Original Invoice</h2>
      <div className="flex gap-3 items-end">
        <Input
          id="return-invoice-id"
          type="text"
          label="Invoice ID or number"
          className="flex-1"
          placeholder="Invoice ID or number"
          value={invoiceRef}
          onChange={e => setInvoiceRef(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && lookup()}
        />
        <button className={btn('primary')} onClick={lookup} disabled={loading}>
          {loading ? 'Looking up…' : 'Find'}
        </button>
      </div>
    </div>
  );
}

/* ─── Return Lines ───────────────────────────────────────────────────────── */
function ReturnLineEditor({
  lines,
  currencyCode,
  onChange,
}: {
  lines: ReturnLine[];
  currencyCode: string;
  onChange: (lines: ReturnLine[]) => void;
}) {
  const toggle = (idx: number) =>
    onChange(lines.map((l, i) => (i === idx ? { ...l, selected: !l.selected } : l)));
  const setQty = (idx: number, qty: number) =>
    onChange(lines.map((l, i) => (i === idx ? { ...l, returnQty: Math.min(Math.max(1, qty), l.maxQty) } : l)));

  return (
    <div className={card}>
      <h2 className="mb-4 text-base font-semibold text-slate-900 dark:text-white">Select Items to Return</h2>
      <div className="space-y-2">
        {lines.map((line, i) => (
          <label
            key={line.itemId}
            className={`flex cursor-pointer items-center gap-4 rounded-xl border-2 p-3 transition ${
              line.selected
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30'
                : 'border-slate-200 dark:border-slate-700'
            }`}
          >
            <input
              type="checkbox"
              className="h-4 w-4 rounded accent-primary-600"
              checked={line.selected}
              onChange={() => toggle(i)}
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-900 dark:text-white">{line.productName}</p>
              <p className="text-xs text-slate-500">
                {formatCurrency(line.unitPrice, currencyCode)} × {line.maxQty} purchased
              </p>
            </div>
            {line.selected && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
                  onClick={e => { e.preventDefault(); setQty(i, line.returnQty - 1); }}
                >
                  −
                </button>
                <span className="min-w-[2ch] text-center text-sm font-bold">{line.returnQty}</span>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
                  onClick={e => { e.preventDefault(); setQty(i, line.returnQty + 1); }}
                >
                  +
                </button>
              </div>
            )}
            <p className="w-24 text-right text-sm font-bold text-primary-600 dark:text-primary-400">
              {line.selected ? formatCurrency(line.unitPrice * line.returnQty, currencyCode) : '—'}
            </p>
          </label>
        ))}
      </div>
    </div>
  );
}

/* ─── Main Returns Page ──────────────────────────────────────────────────── */
export function PosReturnsPage() {
  const store = useAppSelector(s => s.app.selectedStore);
  const currencyCode = store?.currencyCode ?? 'INR';

  if (!store) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center overflow-y-auto p-6 text-center text-slate-500">
        Select a store before processing returns.
      </div>
    );
  }

  return <PosReturnsContent store={store} currencyCode={currencyCode} />;
}

function PosReturnsContent({
  store,
  currencyCode,
}: {
  store: { id: number; name?: string; currencyCode?: string };
  currencyCode: string;
}) {

  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [lines, setLines] = useState<ReturnLine[]>([]);
  const [returnType, setReturnType] = useState<ReturnType>('RETURN');
  const [refundMethod, setRefundMethod] = useState<RefundMethod>('CASH');
  const [restockApproved, setRestockApproved] = useState(true);
  const [reason, setReason] = useState('');
  const [approvalId, setApprovalId] = useState<number | null>(null);
  const [approvalOpen, setApprovalOpen] = useState(false);

  const handleInvoiceFound = (inv: InvoiceDetail) => {
    setInvoice(inv);
    setLines(
      (inv.items ?? []).map(item => ({
        itemId: item.id,
        productId: item.productId,
        productName: item.productName,
        maxQty: item.quantity,
        returnQty: item.quantity,
        unitPrice: item.unitPrice,
        selected: true,
      })),
    );
  };

  const selectedLines = lines.filter(l => l.selected);
  const returnTotal = selectedLines.reduce((s, l) => s + l.unitPrice * l.returnQty, 0);

  const submitReturn = (approvalInstanceId?: number) => {
    if (!invoice || !selectedLines.length) throw new Error('Nothing to return');
    const body: SalesReturnRequest = {
      invoiceId: invoice.id,
      reason,
      returnType,
      refundPayment: true,
      refundMethod,
      restockApproved,
      approvalInstanceId,
      items: selectedLines.map((l) => ({
        productId: l.productId,
        quantity: l.returnQty,
        amount: l.unitPrice * l.returnQty,
      })),
    };
    return billingApi.processSalesReturn(body);
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (approvalInstanceId?: number) => submitReturn(approvalInstanceId),
    onSuccess: () => {
      notify.success(`${returnType === 'EXCHANGE' ? 'Exchange' : 'Return'} processed successfully`);
      setInvoice(null);
      setLines([]);
      setReason('');
    },
    onError: (err: unknown) => {
      const msg = getApiErrorMessage(err, 'Return failed');
      const id = parseApprovalId(msg);
      if (id) {
        setApprovalId(id);
        setApprovalOpen(true);
        notify.error('Supervisor approval required');
      } else {
        notify.error(msg);
      }
    },
  });

  return (
    <div className="h-full min-h-0 overflow-y-auto">
      <div className="mx-auto max-w-2xl space-y-5 p-4 pb-8 sm:p-6">
      <PageHeader
        title="Returns & Exchanges"
        description="Process customer returns or exchanges"
      />

      <InvoiceLookup storeId={store.id} onFound={handleInvoiceFound} />

      {invoice && !(invoice.items?.length) && (
        <div className={card}>
          <p className="text-sm text-slate-500">This invoice has no line items to return.</p>
        </div>
      )}

      {invoice && (invoice.items?.length ?? 0) > 0 && (
        <>
          {/* Invoice Summary */}
          <div className={`${card} flex items-center justify-between`}>
            <div>
              <p className="text-xs text-slate-500">Invoice</p>
              <p className="text-base font-bold text-slate-900 dark:text-white">#{invoice.invoiceNumber}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Original Total</p>
              <p className="text-base font-bold text-primary-600">{formatCurrency(invoice.totalAmount, currencyCode)}</p>
            </div>
          </div>

          <ReturnLineEditor lines={lines} currencyCode={currencyCode} onChange={setLines} />

          {/* Return Options */}
          <div className={card}>
            <h2 className="mb-4 text-base font-semibold text-slate-900 dark:text-white">Return Options</h2>
            <div className="space-y-4">
              {/* Return Type */}
              <div>
                <p className={lbl}>Return Type</p>
                <div className="flex gap-3">
                  {(['RETURN', 'EXCHANGE'] as ReturnType[]).map(t => (
                    <label key={t} className={`flex cursor-pointer items-center gap-2 rounded-xl border-2 px-4 py-2.5 transition ${
                      returnType === t ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30' : 'border-slate-200 dark:border-slate-700'
                    }`}>
                      <input type="radio" className="accent-primary-600" checked={returnType === t} onChange={() => setReturnType(t)} />
                      <span className="text-sm font-medium text-slate-900 dark:text-white">{t === 'RETURN' ? '↩ Refund' : '🔄 Exchange'}</span>
                    </label>
                  ))}
                </div>
              </div>

              {returnType === 'EXCHANGE' && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Exchange records the return credit. Ring replacement items at billing to complete the swap.
                </p>
              )}

              {/* Refund Method */}
              {returnType === 'RETURN' && (
                <div>
                  <p className={lbl}>Refund Method</p>
                  <Select
                    value={refundMethod}
                    onChange={(v) => setRefundMethod(v as RefundMethod)}
                    options={[
                      { value: 'CASH', label: 'Cash' },
                      { value: 'UPI', label: 'UPI' },
                      { value: 'CARD', label: 'Card (Original)' },
                      { value: 'WALLET', label: 'Store Wallet' },
                    ]}
                  />
                </div>
              )}

              {/* Restock */}
              <label className="flex cursor-pointer items-center gap-3">
                <div
                  className={`relative h-5 w-9 rounded-full transition-colors ${restockApproved ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                  onClick={() => setRestockApproved(!restockApproved)}
                >
                  <div className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${restockApproved ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">Restock inventory</p>
                  <p className="text-xs text-slate-500">Uncheck for damaged/unsellable goods</p>
                </div>
              </label>

              {/* Reason */}
              <Input type="text" label="Reason" placeholder="Customer reason for return..." value={reason} onChange={e => setReason(e.target.value)} />
            </div>
          </div>

          {/* Summary + Submit */}
          {selectedLines.length > 0 && (
            <div className={card}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Return Total</p>
                  <p className="text-2xl font-black text-slate-900 dark:text-white">
                    {formatCurrency(returnTotal, currencyCode)}
                  </p>
                  <p className="text-xs text-slate-500">{selectedLines.length} item(s) · {returnType === 'RETURN' ? `Refund via ${refundMethod}` : 'Exchange'}</p>
                </div>
                <button className={`${btn('danger')} py-3 px-6`} onClick={() => mutate(undefined)} disabled={isPending || !selectedLines.length}>
                  {isPending ? 'Processing…' : `Process ${returnType === 'EXCHANGE' ? 'Exchange' : 'Return'}`}
                </button>
              </div>
            </div>
          )}
        </>
      )}
      <PosApprovalModal
        open={approvalOpen}
        approvalId={approvalId}
        title="Return requires approval"
        onClose={() => setApprovalOpen(false)}
        onApproved={(id) => {
          setApprovalOpen(false);
          mutate(id);
        }}
      />
      </div>
    </div>
  );
}
